import React, { useCallback, useRef } from 'react';
import { Box, Button, FormField, Modal, SpaceBetween } from '@amzn/awsui-components-core';
import { TiptapOutputType } from '@amzn/awscat-editor';
import EditorWrapper from '../../editor/EditorWrapper';
import { TiptapMenuType } from '../../editor/utils';

interface ImageAltEntitiesModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSave: (altEntities: TiptapOutputType) => void;
  initialValue?: TiptapOutputType;
}

/**
 * Modal for editing image alt text with entity support.
 * Uses EditorWrapper (same as short title fields) for consistent look and feel.
 * Stores TiptapOutputType which gets converted to entity-placeholder format at publish time.
 */
const ImageAltEntitiesModal: React.FC<ImageAltEntitiesModalProps> = ({
  visible,
  onDismiss,
  onSave,
  initialValue,
}) => {
  const altEntitiesRef = useRef<TiptapOutputType>(initialValue || '');

  // When the modal becomes visible, reset the ref to the latest initialValue
  const prevVisibleRef = useRef(false);
  if (visible && !prevVisibleRef.current) {
    altEntitiesRef.current = initialValue || '';
  }
  prevVisibleRef.current = visible;

  const handleSave = useCallback(() => {
    onSave(altEntitiesRef.current);
  }, [onSave]);

  const handleModelChange = useCallback((value: TiptapOutputType) => {
    altEntitiesRef.current = value;
  }, []);

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Edit Alt Text"
      data-testid="image-alt-entities-modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
      closeAriaLabel="Close modal"
    >
      <FormField
        label="Alt text with entities"
        description="Describe the image using entity references for downstream processing. Entities will be resolved to their current values at publish time."
        stretch
      >
        {visible && (
          <EditorWrapper
            model={initialValue || ''}
            readOnly={false}
            onModelChange={handleModelChange}
            menuType={TiptapMenuType.Hide}
            removeFormatting
            singleLine
          />
        )}
      </FormField>
    </Modal>
  );
};

export default ImageAltEntitiesModal;
